import React, { useRef, useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import {
  Container, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Text, Tabs, TabList, Tab, Table, Thead, Tbody, Tr, Th, Td, Avatar, Box, Button, Flex, Icon, Spinner, Center
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { FiMoreHorizontal } from 'react-icons/fi';
import LineupManagement from '../components/LineupManagement';
import PageTitle from '../components/PageTitle';
import { useTeamRoster } from '../hooks/useTeamRoster';
import { useTeam } from '../hooks/useTeam';
import TeamLogo from '../components/TeamLogo';
import { getColor } from '../utils/colorUtils';
import { getTeamName, getTeamLogo } from '../utils/getTeamInfo';
import SelectTeam from '../components/SelectTeam';
import { useMyLeagueStore, Team } from '../store/myLeagueStore';
import PitcherManagement from '../components/PitcherManagement';
import DepthChart from '../components/DepthChart';
import { useUserContext } from '../contexts/UserContext';
import ErrorBoundary from '../components/ErrorBoundary';
import PlayerCard from '../components/PlayerCard';
import { Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure } from '@chakra-ui/react';
import { ROSTER_SECTIONS_CONFIG } from '../constants';
import WarningPanel from '../components/WarningPanel';
import { ValidateTeam } from '../utils/ValidateTeam';
import RosterContent from '../components/RosterContent';
import { FrontOffice } from '../components/FrontOffice';
import UpgradeShop from '../components/UpgradeShop';
import { isAdmin } from '../hooks/useUser';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

export interface Contract {
  year_signed: number | null;
  current_salary: number;
  salaries: Array<{ year: number; salary: number }>;
  contract_types: Array<{ year: number; type: string }>;
  buyouts: Array<{ year: number; amount: number }>;
  total_value?: number; // Added for total contract value
}

interface RosterEntry {
  id: number;
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2: string;
    position_3: string;
    mlb_team: string;
    image?: string;
    bats?: string;
    throws?: string;
    rule_five_eligibility?: string;
  };
  level: string;
  status: string;
  status_until: string | null;
  playerContract: Contract;
  is_40_man: boolean;
  year: number;
  deal_status?: string | null;
  deal_until?: string | null;
}



const RosterPageContent: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { myTeam, currentLeague, systemSettings } = useUserContext();
  const [currentTeamId, setCurrentTeamId] = React.useState<number | null>(null);
  // Use myLeague for league context (simplified)
  const myLeague = useMyLeagueStore(state => state.myLeague);
  const location = useLocation();
  const isAdminFlag = isAdmin();
  // On initial load, set currentTeamId to myTeam if null
  useEffect(() => {
    // Set currentTeamId to myTeam if it's null or different
    if (myTeam && currentTeamId === null) {
      setCurrentTeamId(Number(myTeam));
    }
  }, [myTeam, currentTeamId]);

  useEffect(() => {
    setCurrentTeamId(Number(myTeam));
  }, [myTeam]);

  // Ensure whenever we land on /roster, we switch to my team if different
  useEffect(() => {
    if (location.pathname.startsWith('/roster') && myTeam && Number(currentTeamId) !== Number(myTeam)) {
      setCurrentTeamId(Number(myTeam));
    }
  }, [location.pathname]);

  // Listen for custom event to switch to my team
  useEffect(() => {
    const handleSwitchToMyTeam = (event: CustomEvent) => {
      if (myTeam) {
        setCurrentTeamId(Number(myTeam));
        // If there's a tab parameter in the event detail, switch to that tab
        if (event.detail) {
          const tabName = event.detail.tab;
          const tabNames = [
            'Roster Management',
            'Contract Details',
            'Additional Info',
            'Lineup',
            'Pitchers',
            'Depth Chart',
            'Front Office',
            'Upgrade'
          ];

          if (tabName) {
            // If there's a specific tab parameter, find the matching tab
            const matchingTab = tabNames.find(t => t.toLowerCase().replace(/ /g, '') === tabName.toLowerCase().replace(/ /g, ''));
            if (matchingTab) {
              setActiveTab(matchingTab);
            }
          } else {
            // If no tab parameter (e.g., just /roster), default to Roster Management
            setActiveTab('Roster Management');
          }
        }
      }
    };

    window.addEventListener('switchToMyTeam', handleSwitchToMyTeam as EventListener);

    return () => {
      window.removeEventListener('switchToMyTeam', handleSwitchToMyTeam as EventListener);
    };
  }, [myTeam]);

  const { players, total_payroll, activeRosterSize, fortyManRosterSize, hasFranchiseTag, hasQualifyingOffer, rosterLocked, loading: rosterLoading, error: rosterError, refetch, rosterMove } = useTeamRoster(currentTeamId || 1);
  const [isSelectTeamOpen, setSelectTeamOpen] = useState(false);

  // Use the useTeam hook to fetch team information
  const teamIdNumber = typeof currentTeamId === 'string' ? Number(currentTeamId) : currentTeamId;
  const { team: teamData, isLoading: teamLoading, error: teamError, refetch: refetchTeam } = useTeam(teamIdNumber || undefined);
  const team = teamData as Team | null;

  // Refetch salary cap when navigating to the roster page
  useEffect(() => {
    if (location.pathname.startsWith('/roster') && teamIdNumber) {
      refetchTeam();
    }
  }, [location.pathname, teamIdNumber, refetchTeam]);

  const handleSelectTeam = (teamId: string) => {
    setCurrentTeamId(Number(teamId));
    setSelectTeamOpen(false);
    // Reset to Roster Management tab when switching teams
    setActiveTab('Roster Management');
  };


  // Remove complex leagueTeam logic - just use the team data from useTeam hook
  // The team ID comes from userStore.myTeam, which is the single source of truth

  // Tab logic: allow ?tab= or ?t= parameter to set default tab
  const tabParam = searchParams.get('tab') || searchParams.get('t');
  const tabNames = [
    'Roster Management',
    'Contract Details',
    'Additional Info',
    'Lineup',
    'Pitchers',
    'Depth Chart',
    'Front Office',
    'Upgrade'
  ];

  // Map abbreviations to full tab names
  const tabAbbreviations: Record<string, string> = {
    'rm': 'Roster Management',
    'ai': 'Additional Info',
    'cd': 'Contract Details',
    'l': 'Lineup',
    'p': 'Pitchers',
    'dc': 'Depth Chart',
    'fo': 'Front Office',
    'u': 'Upgrade'
  };

  const getTabFromParam = (param: string): string => {
    const lowerParam = param.toLowerCase().replace(/ /g, '');
    // First check if it's an abbreviation
    if (tabAbbreviations[lowerParam]) {
      return tabAbbreviations[lowerParam];
    }
    // Otherwise try to match the full tab name
    return tabNames.find(t => t.toLowerCase().replace(/ /g, '') === lowerParam) || 'Roster Management';
  };

  const defaultTab = tabParam ? getTabFromParam(tabParam) : 'Roster Management';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Watch for URL parameter changes and update active tab
  useEffect(() => {
    const newTabParam = searchParams.get('tab') || searchParams.get('t');
    if (newTabParam) {
      const newTab = getTabFromParam(newTabParam);
      setActiveTab(newTab);
    } else {
      // No tab parameter means default to "Roster Management"
      setActiveTab('Roster Management');
    }
  }, [searchParams]);

  const topRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   window.scrollTo({ top: 0, behavior: 'smooth' });
  // }, [teamId]);

  const fetchRosterAgain = () => {
    refetch();
  };

  const getRosterByStatus = (status: string) => {
    return players.filter(entry => entry.status === status);
  };

  const getRosterByLevel = (level: string) => {
    return players.filter(entry => entry.level === level);
  };

  // Add state for selected player and modal
  const [selectedPlayer, setSelectedPlayer] = React.useState<RosterEntry | null>(null);
  const [selectedSubListLabel, setSelectedSubListLabel] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const renderRosterTable = (rosterEntries: RosterEntry[], label?: string, isActiveRoster = false) => {
    const barBg = getColor(team?.color_primary, '#222');
    const barText = getColor(team?.color_text, '#fff');
    const barBorder = getColor(team?.color_secondary, 'transparent');

    const sectionConfig = ROSTER_SECTIONS_CONFIG.find(sec => sec.label === label);

    return (
      <Box mb={8}>
        {label && (
          <Flex
            align="center"
            bg={barBg}
            color={barText}
            border={barBorder !== 'transparent' ? '1.5px solid' : undefined}
            borderColor={barBorder}
            px={4}
            py={2}
            borderRadius="md"
            fontWeight="bold"
            fontSize="lg"
            mb={2}
          >
            {label}
          </Flex>
        )}
        <Table variant="simple" size="sm" bg="whiteAlpha.100" borderRadius="md">
          <Thead>
            <Tr>
              <Th>Player</Th>
              <Th>POS</Th>
              <Th>Salary</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {rosterEntries.length === 0 ? (
              <Tr>
                <Td colSpan={4} textAlign="center" color="gray.400">(No Players)</Td>
              </Tr>
            ) : (
              rosterEntries.map(entry => {
                const player = entry.rwPlayer;
                // Concatenate all non-empty positions
                const positions = [player.position_1, player.position_2, player.position_3]
                  .filter(pos => !!pos && pos !== '-')
                  .join("/");
                return (
                  <Tr
                    key={entry.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setSelectedPlayer(entry); setSelectedSubListLabel(label || ''); onOpen(); }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(44, 130, 201, 0.15)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <Td pl={2} pr={0}>
                      <Flex align="center">
                        {/* Only show outlined diamond if not Active Roster and status is on40Man */}
                        {label !== 'Active Roster' && sectionConfig?.on40Man && (
                          <Box as="span" color="blue.600" fontWeight="bold" fontSize="lg" mr="3">◇</Box>
                        )}
                        <Avatar
                          size="md"
                          src={httpsPlayerImageUrl(player.image)}
                          name={`${player.first_name} ${player.last_name}`}
                          mr={2}
                          ignoreFallback={false}
                          onError={() => {
                            if (player.image) {
                              player.image = '';
                            }
                          }}
                        />
                        {`${player.first_name} ${player.last_name}`}
                      </Flex>
                    </Td>
                    <Td>{positions}</Td>
                    <Td>${entry.playerContract?.current_salary?.toLocaleString() || 0}</Td>
                    <Td><Icon as={FiMoreHorizontal} /></Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
        <Flex justify="flex-end" mt={2}>
          <Button size="xs" onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}>
            Back To Top
          </Button>
        </Flex>
      </Box>
    );
  };

  if (teamLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }



  if (rosterError) {
    return (
      <Center h="100vh">
        <Text color="red.500">Error loading roster data</Text>
      </Center>
    );
  }


  const isMyTeam = currentTeamId === Number(myTeam);
  const lineupTabsDisabledForOtherTeam =
    !!(currentTeamId && myTeam && currentTeamId !== Number(myTeam)) && !isAdminFlag;
  /** Lineup / pitchers / depth must use the roster being viewed, not always myTeam (admins can open other teams). */
  const viewedTeamIdForLineup =
    currentTeamId != null ? String(currentTeamId) : (myTeam?.toString() ?? '');
  const teamName = isMyTeam ? 'My Team' : getTeamName(team);


  // --- Robust section assignment ---
  // Map: section label -> array of players
  const sectionPlayers: { [label: string]: RosterEntry[] } = {};
  const assigned = new Set<number>();
  ROSTER_SECTIONS_CONFIG.forEach(section => {
    sectionPlayers[section.label] = [];
  });


  // Find the order number for AAA
  const AAA_ORDER = ROSTER_SECTIONS_CONFIG.find(sec => sec.label === 'AAA')?.order ?? 11;

  players.forEach(player => {
    let matched = false;

    // Priority check: If player has "Awaiting Assignment" status, skip all other sections
    if (player.status === 'Awaiting Assignment' ||
      (player.deal_status && player.deal_status.toLowerCase().includes('awaiting_assignment'))) {
      // Don't assign to any section - they'll go to unassigned and then to Awaiting Assignment section
      return;
    }

    // Priority check: If player has free agent deal_status, skip all other sections
    if (player.deal_status && player.deal_status.toLowerCase().includes('free')) {
      // Don't assign to any section - they'll go to unassigned and then to Free Agents Under Contract
      return;
    }

    // Check if player has a carried contract for the current team
    // Players with carried contracts should not appear in Active Roster or other regular sections
    const hasCarriedContract = (player as any).DominusContracts?.some((contract: any) =>
      contract.carried === true &&
      contract.team_id === currentTeamId
    );

    for (const section of ROSTER_SECTIONS_CONFIG) {
      let match = false;
      if (section.order < AAA_ORDER) {
        // MLB-level sections: require MLB level and status match
        match =
          player.level?.toLowerCase() === 'mlb' &&
          section.status.some(
            s => s === player.status?.toLowerCase()
          );

        // Exclude players with carried contracts from Active Roster and other MLB sections
        if (match && hasCarriedContract && player.team_id !== currentTeamId) {
          match = false;
        }
      } else {
        // Minor league sections: match by level only (status arrays are legacy/unused)
        // Check if level matches or if it's in the levelAliases array
        const playerLevel = player.level?.toLowerCase();
        match = section.level.toLowerCase() === playerLevel;
        if (!match && (section as any).levelAliases) {
          match = (section as any).levelAliases.some((alias: string) =>
            alias.toLowerCase() === playerLevel
          );
        }

        // Exclude players with carried contracts from minor league sections as well
        if (match && hasCarriedContract) {
          match = false;
        }
      }
      if (match) {
        sectionPlayers[section.label].push(player);
        assigned.add(player.id);
        matched = true;
        break;
      }
    }
    if (!matched) {
      // Optionally handle unassigned players
    }
  });
  // Collect unassigned players
  const unassignedPlayers = players.filter(p => !assigned.has(p.id));

  // Off Season: add Qualified for Offer section above Active Roster
  const isOffSeasonEFQO = systemSettings?.seasonStatus === 'Off Season EFQO';
  const qualifiedForOfferPlayers = players.filter(p => p.deal_status === 'efqo' || p.deal_status === 'pending_fa_offer' || p.deal_status === 'qualifying_offer_made');
  const sectionsConfigWithQO = isOffSeasonEFQO
    ? [
      {
        order: -1,
        level: 'MLB',
        status: ['efqo'],
        label: 'Pending Free Agency',
        actionMenu: undefined,
        on40Man: false,
      },
      ...ROSTER_SECTIONS_CONFIG,
    ]
    : ROSTER_SECTIONS_CONFIG;
  const sectionPlayersWithQO = {
    ...sectionPlayers,
    ['Pending Free Agency']: qualifiedForOfferPlayers,
  };

  // Find the Active Roster section label
  const activeRosterLabel = ROSTER_SECTIONS_CONFIG.find(sec => sec.actionMenu === 'activeRoster')?.label || 'Active Roster';
  const activeRosterCount = sectionPlayers[activeRosterLabel]?.length || 0;

  const minorLeagueSectionCounts = ROSTER_SECTIONS_CONFIG
    .filter(section => section.order >= AAA_ORDER && section.actionMenu === 'minorLeague')
    .map(section => ({
      label: section.label,
      count: sectionPlayers[section.label]?.length || 0,
    }));

  const validation = ValidateTeam(
    fortyManRosterSize || 0,
    activeRosterSize,
    minorLeagueSectionCounts
  );

  return (
    <Container maxW="container.xl" py={8}>
      <div ref={topRef} />
      <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={6}>
        {currentLeague && (
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/dashboard" color="blue.500">
              {currentLeague.league_name} League
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}

        <BreadcrumbItem isCurrentPage>
          <Text color="gray.500">
            {(!currentTeamId || (currentTeamId === Number(myTeam))) ? "My Team" : `${teamName} Roster`}
          </Text>
        </BreadcrumbItem>
      </Breadcrumb>
      <Flex align="center" justify="space-between" mb={4}>
        <PageTitle>
          {team && (
            <Box mr={3}>
              <TeamLogo team={{
                name: getTeamName(team),
                abbreviation: team.abbreviation,
                logo: getTeamLogo(team),
                color_primary: team.color_primary,
                color_secondary: team.color_secondary,
                color_text: team.color_text
              }} size="md" />
            </Box>
          )}
          {currentTeamId === Number(myTeam)
            ? <>My Team: {getTeamName(team)} Roster</>
            : <>{getTeamName(team)} Roster</>
          }
        </PageTitle>
        {myTeam && (
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => setSelectTeamOpen(true)}
          >
            CHANGE TEAMS
          </Button>
        )}
      </Flex>
      {/* Show validation warnings/errors for My Team only */}
      {isMyTeam && !validation.valid && (
        <WarningPanel type={validation.type || 'warning'} title={validation.type === 'error' ? 'Team Error' : 'Team Warning'} messages={validation.message} />
      )}
      <Tabs colorScheme="blue" variant="enclosed" index={tabNames.indexOf(activeTab)} onChange={(index) => setActiveTab(tabNames[index])}>
        <TabList>
          <Tab sx={{ borderBottomRadius: 0 }}>Roster Management</Tab>
          <Tab sx={{ borderBottomRadius: 0 }}>Contract Details</Tab>
          <Tab sx={{ borderBottomRadius: 0 }}>Additional Info</Tab>
          <Tab sx={{ borderBottomRadius: 0 }} isDisabled={lineupTabsDisabledForOtherTeam}>Lineup</Tab>
          <Tab sx={{ borderBottomRadius: 0 }} isDisabled={lineupTabsDisabledForOtherTeam}>Pitchers</Tab>
          <Tab sx={{ borderBottomRadius: 0 }} isDisabled={lineupTabsDisabledForOtherTeam}>Depth Chart</Tab>
          <Tab sx={{ borderBottomRadius: 0 }}>Front Office</Tab>
        </TabList>
        {(() => {
          const activeTabIndex = tabNames.indexOf(activeTab);

          if (activeTabIndex === 0) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <RosterContent
                    tabName="Roster Management"
                    team={team}
                    leagueTeam={team}
                    sectionPlayers={sectionPlayersWithQO}
                    unassignedPlayers={unassignedPlayers}
                    sectionsConfig={sectionsConfigWithQO}
                    activeRosterLabel={activeRosterLabel}
                    activeRosterCount={activeRosterCount}
                    onBackToTop={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    isMyTeam={isMyTeam}
                    totalPayroll={total_payroll}
                    refetch={fetchRosterAgain}
                    rosterMove={rosterMove}
                    currentTeamId={currentTeamId}
                    fortyManRosterSize={fortyManRosterSize}
                    hasFranchiseTag={hasFranchiseTag}
                    hasQualifyingOffer={hasQualifyingOffer}
                    rosterLocked={rosterLocked}
                  />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 1) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <RosterContent
                    tabName="Contract Details"
                    team={team}
                    leagueTeam={team}
                    sectionPlayers={sectionPlayersWithQO}
                    unassignedPlayers={unassignedPlayers}
                    sectionsConfig={sectionsConfigWithQO}
                    activeRosterLabel={activeRosterLabel}
                    activeRosterCount={activeRosterCount}
                    onBackToTop={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    isMyTeam={isMyTeam}
                    totalPayroll={total_payroll}
                    refetch={fetchRosterAgain}
                    rosterMove={rosterMove}
                    currentTeamId={currentTeamId}
                    activeRosterSize={activeRosterSize}
                    fortyManRosterSize={fortyManRosterSize}
                    hasFranchiseTag={hasFranchiseTag}
                    hasQualifyingOffer={hasQualifyingOffer}
                    rosterLocked={rosterLocked}
                  />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 2) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <RosterContent
                    tabName="Additional Info"
                    team={team}
                    leagueTeam={team}
                    sectionPlayers={sectionPlayersWithQO}
                    unassignedPlayers={unassignedPlayers}
                    sectionsConfig={sectionsConfigWithQO}
                    activeRosterLabel={activeRosterLabel}
                    activeRosterCount={activeRosterCount}
                    onBackToTop={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    isMyTeam={isMyTeam}
                    totalPayroll={total_payroll}
                    refetch={fetchRosterAgain}
                    rosterMove={rosterMove}
                    currentTeamId={currentTeamId}
                    activeRosterSize={activeRosterSize}
                    fortyManRosterSize={fortyManRosterSize}
                    hasFranchiseTag={hasFranchiseTag}
                    hasQualifyingOffer={hasQualifyingOffer}
                    rosterLocked={rosterLocked}
                  />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 3) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <LineupManagement teamId={viewedTeamIdForLineup} onSwitchToPitchersTab={() => setActiveTab('Pitchers')} />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 4) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <PitcherManagement teamId={viewedTeamIdForLineup} />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 5) {
            return (
              <Box>
                {rosterLoading ? (
                  <Center py={8}>
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={4} color="gray.500">Loading roster...</Text>
                  </Center>
                ) : (
                  <DepthChart teamId={viewedTeamIdForLineup} />
                )}
              </Box>
            );
          }

          if (activeTabIndex === 6) {
            return (
              <FrontOffice teamId={currentTeamId} leagueId={myLeague?.id} teamName={teamName} />
            );
          }

          if (activeTabIndex === 7) {
            return (
              <Box>
                <UpgradeShop
                  rosterLoading={rosterLoading}
                  totalPayroll={total_payroll}
                  team={team}
                />
              </Box>
            );
          }

          return null;
        })()}
      </Tabs>

      {/* SelectTeam Modal */}
      {isSelectTeamOpen && (
        <SelectTeam
          isOpen={isSelectTeamOpen}
          onClose={() => setSelectTeamOpen(false)}
          myTeam={myTeam ?? undefined}
          onSelect={handleSelectTeam}
          league={(myLeague || { conferences: [] }) as any}
        />
      )}

      {/* PlayerCard Modal */}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
          <ModalOverlay />
          <ModalContent bg="transparent" boxShadow="none" border="none">
            <ModalBody p={0}>
              {selectedPlayer && (
                <PlayerCard
                  player={selectedPlayer}
                  team={team || undefined}
                  subListLabel={selectedSubListLabel || ''}
                  onClose={onClose}
                  isMyTeam={isMyTeam}
                  refetch={fetchRosterAgain}
                  rosterLocked={rosterLocked}
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

const RosterPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <RosterPageContent />
    </ErrorBoundary>
  );
};

export default RosterPage; 